import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { tourSteps } from './tourSteps';

const DemoTourContext = createContext(null);

const STORAGE_KEY = 'demo_tour_state';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function DemoTourProvider({ children, enabled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState('explain'); // 'prompt' | 'explain'

  // Auto-start once after demo login (flag set by LoginPage)
  useEffect(() => {
    if (!enabled) return;
    if (localStorage.getItem('start_demo_tour') === '1') {
      localStorage.removeItem('start_demo_tour');
      setStepIndex(0);
      setPhase('explain');
      setActive(true);
      saveState({ active: true, stepIndex: 0, phase: 'explain' });
      return;
    }
    const saved = loadState();
    if (saved?.active) {
      setStepIndex(saved.stepIndex || 0);
      setPhase(saved.phase || 'explain');
      setActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // While waiting on a "prompt" step, watch for the visitor clicking the highlighted nav item
  useEffect(() => {
    if (!active || phase !== 'prompt') return;
    const step = tourSteps[stepIndex];
    if (step?.route && location.pathname === step.route) {
      setPhase('explain');
      saveState({ active: true, stepIndex, phase: 'explain' });
    }
  }, [location.pathname, active, phase, stepIndex]);

  const goToStep = useCallback((index) => {
    const step = tourSteps[index];
    if (!step) return;
    const nextPhase = step.navTestId ? 'prompt' : 'explain';
    setStepIndex(index);
    setPhase(nextPhase);
    saveState({ active: true, stepIndex: index, phase: nextPhase });
  }, []);

  const start = useCallback(() => {
    setActive(true);
    goToStep(0);
  }, [goToStep]);

  const next = useCallback(() => {
    const nextIndex = stepIndex + 1;
    if (nextIndex >= tourSteps.length) {
      setActive(false);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    goToStep(nextIndex);
  }, [stepIndex, goToStep]);

  const skip = useCallback(() => {
    setActive(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const finish = useCallback(() => {
    setActive(false);
    localStorage.removeItem(STORAGE_KEY);
    navigate('/onboarding');
  }, [navigate]);

  return (
    <DemoTourContext.Provider value={{ active, stepIndex, phase, step: tourSteps[stepIndex], totalSteps: tourSteps.length, start, next, skip, finish }}>
      {children}
    </DemoTourContext.Provider>
  );
}

export function useDemoTour() {
  return useContext(DemoTourContext);
}
